/**
 * RegistrationZuordnen.tsx - the Zuordnen task of the registration (P5-S5 review round 1): an insurer
 * mail no registration could be matched to. It lists the candidate registrations (Versicherer,
 * Vorgangs-ID, Status) with «Zuordnen» each (mandate.eingangskontrolle only); a pick closes the task
 * and opens a review task «Eintragungsbestätigung prüfen» for that registration.
 *
 * The data comes from the caller (the Aufgaben peek reads GET registration-zuordnen and mounts this
 * only when it answers).
 */

import { useState } from 'react';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import type { RegistrationZuordnenDto } from '@brokly/shared';
import { Button } from '../../components/ui/Button';
import { useEffectivePermissions } from '../administration/useEffectivePermissions';
import { useZuordnenRegistration } from './mandate-registration-queries';
import { REGISTRATION_DECISION_KEY } from './MandateRegistrationPanel';
import { registrationErrorText } from './registration-error-text';

export function RegistrationZuordnen({ taskId, data }: { taskId: string; data: RegistrationZuordnenDto }) {
  const { can } = useEffectivePermissions();
  const canDecide = can(REGISTRATION_DECISION_KEY);
  const zuordnen = useZuordnenRegistration(taskId);
  const [done, setDone] = useState(false);

  return (
    <div className="flex flex-col gap-2" data-testid="reg-zuordnen">
      {data.candidates.length === 0 ? (
        <p className="text-sm text-muted-foreground">Keine offene Meldung kommt für diese E-Mail in Frage.</p>
      ) : (
        <ul className="flex flex-col gap-1">
          {data.candidates.map((c) => (
            <li
              key={c.registration_id}
              className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-background px-2.5 py-1.5 text-sm"
              data-testid={`reg-zuordnen-${c.registration_id}`}
            >
              <span className="min-w-0 flex-1 truncate font-medium">{c.carrier_name ?? 'Versicherer unbekannt'}</span>
              <span className="text-xs text-muted-foreground">{c.vorgangs_ref}</span>
              <span className="text-xs">{c.status_label}</span>
              {canDecide && !done && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={zuordnen.isPending}
                  onClick={() => zuordnen.mutate({ registrationId: c.registration_id }, { onSuccess: () => setDone(true) })}
                >
                  Zuordnen
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
      {done && (
        <p className="flex items-center gap-2 text-sm text-status-success" role="status">
          <CheckCircle2 className="size-4" />
          Zugeordnet, Prüfaufgabe erstellt
        </p>
      )}
      {zuordnen.isError && (
        <p className="flex items-center gap-2 text-sm text-destructive" role="alert">
          <AlertTriangle className="size-4" />
          {registrationErrorText(zuordnen.error)}
        </p>
      )}
    </div>
  );
}
